import { Component, OnInit, OnChanges, Input, ViewEncapsulation, SimpleChange, SimpleChanges, signal, computed, effect } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FlowManagementService } from '../services/flow-management.service';
import { FlowEventService } from '../services/flow-event.service';
import { StorageRouterService } from '../services/storage-router.service';
import { DialogService, DialogRef } from '@progress/kendo-angular-dialog';
import { Flow } from '../obj/Flow';
import { DiffEditorModel } from 'ngx-monaco-editor-v2';

@Component({
    selector: 'app-flow-diff',
    templateUrl: './flow-diff.component.html',
    styleUrls: ['./flow-diff.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class FlowDiffComponent implements OnInit {

  editorOptions = { 
    /*theme: 'vs-dark',*/ 
    language: 'json', 
    automaticLayout: true, 
    renderSideBySide: true,

    tabSize: 1,
    insertSpaces: true,
    wordWrap: 'on',
    fontLigatures: false,
  };
  code: string = '';

  //private _flow = false;
  private _versionLeft = 'current';
  private _versionRight = 'current';

  environmentLeft = signal<string>(undefined);
  environmentRight = signal<string>(undefined);
  flowLeft = signal<string>(undefined);
  flowRight = signal<string>(undefined);
  versionsLeft = signal<any[]>([]);
  versionsRight = signal<any[]>([]);

  public environments: {
    text: string,
    value: string
  }[];

  private flows = signal<any>([]);

  flowsLeft = computed(() => {
    const env = this.environmentLeft();
    return this.flows().filter(flow => flow.environment === env);
  });
  flowsRight = computed(() => {
    const env = this.environmentRight();
    return this.flows().filter(flow => flow.environment === env);
  });

  //@Input() flowRight: string;
  error: string;
  ready = signal(true);
  // originalModel: monaco.editor.ITextModel;
  // modifiedModel: monaco.editor.ITextModel;
  editor: any;

  originalModel: DiffEditorModel = {
    code: '{}',
    language: 'application/json'
  };

  modifiedModel: DiffEditorModel = {
    code: '{}',
    language: 'application/json'
  };

  constructor(
    private flowManagementService: FlowManagementService,
    private flowEventService: FlowEventService,
    private dialogService: DialogService,
    private storageDexieService: StorageRouterService,
    private route: ActivatedRoute,
  ) {
    //this.editorOptions = new JsonEditorOptions()
    //this.editorOptions.modes = ['code', 'tree']; // set all allowed modes
    //this.editorOptions.mode = 'code'; //set only one mode
    //this.data = { "products": [{ "name": "car", "product": [{ "name": "honda", "model": [{ "id": "civic", "name": "civic" }, { "id": "accord", "name": "accord" }, { "id": "crv", "name": "crv" }, { "id": "pilot", "name": "pilot" }, { "id": "odyssey", "name": "odyssey" }] }] }] }

    effect(async() => {
      const flowName = this.flowLeft();
      const env = this.environmentLeft();
      if (!flowName || !env) return;
      this.versionsLeft.set(await this.loadVersionsFor(env, flowName, this.flowsLeft()));
    }, {allowSignalWrites:true});
    effect(async() => {
      const flowName = this.flowRight();
      const env = this.environmentRight();
      if (!flowName || !env) return;
      this.versionsRight.set(await this.loadVersionsFor(env, flowName, this.flowsRight()));
    }, {allowSignalWrites:true});

  }

  @Input() set versionLeft(value) {
    if (Array.isArray(value)) {
      const [environment, flowName, version] = value;
      this.environmentLeft.set(environment);
      this.flowLeft.set(flowName);
      this._versionLeft = version;
    }
    else {
      this._versionLeft = value;
    }
  }
  get versionLeft() {
    return this._versionLeft;
  }

  @Input() 
  set versionRight(value) {
    if (Array.isArray(value)) {
      const [environment, flowName, version] = value;
      this.environmentRight.set(environment);
      this.flowRight.set(flowName);
      this._versionRight = version;
    }
    else {
      this._versionRight = value;
    }
  }
  get versionRight(): string {
    return this._versionRight;
  }

  // @Input()
  // set flow(value:string[]) {
  //   if (!value) {
  //     return;
  //   }
  //   const [environment, flowName, version] = value;

  //   // this._flow = value;

  //   this.environmentLeft.set(environment);
  //   this.flowLeft.set(flowName);

  //   this.environmentRight.set(environment);
  //   this.flowRight.set(flowName);
  //   this._flow = true;
  // }
  // get flow(): boolean {
  //   return this._flow;
  // }

  ngOnInit() {
    // Seed signals from route params when arrived via the diff URL.
    //   /flows/:env/:name/diff/:other-env/:other-name                — versions default to 'current'
    //   /flows/:env/:name/:version/diff/:other-env/:other-name/:other-version
    const p = this.route.snapshot.paramMap;
    const env = p.get('envName');
    const name = p.get('flowName');
    const version = p.get('version') || 'current';
    const oEnv = p.get('otherEnvName');
    const oName = p.get('otherFlowName');
    const oVersion = p.get('otherVersion') || 'current';

    if (env && name && oEnv && oName) {
      // The setters accept either a string version or a [env, flow, version]
      // tuple (see Array.isArray branch above). The getter returns string, so
      // TS narrows the setter accordingly — cast the tuple form through `any`.
      (this as any).versionLeft = [env, name, version];
      (this as any).versionRight = [oEnv, oName, oVersion];
    }

    // Always populate the environment/flow lists so the dropdowns can resolve
    // GUIDs to display names. When all params came from the URL, kick off the
    // diff after those lists land. The dropdowns fall back to showing the raw
    // GUID via `[allowCustom]=true` when there's no matching cached item — no
    // separate "blank state" handling needed.
    this.loadEnvs().then(() => {
      if (this.environmentLeft() && this.environmentRight() && this.flowLeft() && this.flowRight()) {
        this.load();
      }
    });
  }

  /**
   * Build the `[current, ...runs]` versions list for a given env+flowName.
   * Resolves the flow from the dropdown's filtered list when it's in cache;
   * otherwise synthesizes a stub so we can still issue the runs fetch by URL
   * parameters alone (admin-only flows or fresh deep-links to the diff page).
   */
  private async loadVersionsFor(env: string, flowName: string, candidates: any[]): Promise<any[]> {
    let flow: any = (candidates || []).find(f => f.flowName === flowName || f.name === flowName);
    if (!flow) {
      flow = Object.assign(new Flow(), {
        environment: env,
        flowName,
        flowState: 'Started',
        runs: [],
      });
    }

    const versions: any[] = [{ name: 'current', startTime: flow.modified ?? null }];

    if (flow.runs && flow.runs.length > 0) {
      versions.push(...flow.runs);
      return versions;
    }

    try {
      const fetched = await this.flowManagementService.getFlowRuns(
        flow, false, false, false, undefined, true
      );
      if (fetched && fetched.length) versions.push(...fetched);
    } catch {
      // Network failure — leave just 'current' so the dropdown isn't empty.
    }
    return versions;
  }

  async loadEnvs() {

    // Only seed from currentEnvironment when the route didn't already set
    // an env (otherwise we'd clobber `/flows/.../diff/...` URL params).
    if (!this.environmentLeft()) {
      this.environmentLeft.set(this.flowManagementService.currentEnvironment);
    }
    if (!this.environmentRight()) {
      this.environmentRight.set(this.flowManagementService.currentEnvironment);
    }

    // Load BOTH user-scope and admin-scope flows so the dropdown can resolve
    // a flow visible only via /admin (the user-scope `getFlows()` alone misses
    // those, leaving the dropdown empty when the diff URL points at an
    // admin-only flow). Dedupe by env + flowName.
    const pEnvironments = this.flowManagementService.getEnvironments().toPromise();
    const pFlows = Promise.all([
      this.storageDexieService.getFlows(),
      this.storageDexieService.getFlowsAdmin(),
    ]).then(([user, admin]) => {
      const seen = new Set<string>();
      const merged: any[] = [];
      for (const f of [...(user || []), ...(admin || [])]) {
        const key = `${f.environment}/${f.flowName}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(f);
      }
      return merged;
    });
    const [response, flows] = await Promise.all([pEnvironments, pFlows]);

    this.flows.set(flows);
    
    if (response) {
      const envs = response.value.map((item) => {
          return {
              'text': item.properties.displayName,
              'value': item.name,
              'isDefault': item.properties.isDefault
          };
      }).sort((a, b) => a.text.localeCompare(b.text));

      this.environments = envs;
    }
    
  }

  async load() {
    //const r = this.ready;
    try {
      if (!this.environmentLeft() || !this.environmentRight() || !this.flowLeft() || !this.flowRight()) {
        return;
      }
      this.ready.set(false);

      const getDef = (version: string, env: string, flowName: string) => {
        return (version === 'current'
          ? this.flowManagementService.getFlow(env, flowName).toPromise().then(f => f.properties.definition)
          : this.flowManagementService.getFlowFromRun(`${env}/flows/${flowName}/runs/${version}`).toPromise().then(r => r.properties.flow.properties.definition)
        ).then(definition=>{
          if (definition && typeof definition === 'object' && 'metadata' in definition) {
            // remove 'metadata' property from the output
            delete definition.metadata;
          }
          return definition;
        });
      };

      const pLeft  = getDef(this.versionLeft,  this.environmentLeft(),  this.flowLeft());
      const pRight = getDef(this.versionRight, this.environmentRight(), this.flowRight());

      const [left, right] = await Promise.all([pLeft, pRight]);

      this.editor.getOriginalEditor().setValue(JSON.stringify(left, null, 2));
      // this.originalModel = {
      //   code: JSON.stringify(left, null, 2),
      //   language: 'application/json'
      // };
      this.editor.getModifiedEditor().setValue(JSON.stringify(right, null, 2));
      // this.modifiedModel = {
      //   code: JSON.stringify(right, null, 2),
      //   language: 'application/json'
      // };
      // var diffEditor = monaco.editor.createDiffEditor(document.getElementById("container"));
      // diffEditor.setModel({
      //   original: originalModel,
      //   modified: modifiedModel
      // });
    }
    catch(err) {
        this.error = err;
    }
    finally {
      this.ready.set(true);
    }  
  }

  onInit(editor) {
    //let line = editor.getPosition();
    //console.log(line);
    this.editor = editor;
    // this.editor.setValue(this._code);
    // this.editor.layout();
    //debugger
  }

  close() {
    this.flowEventService.closeTabWindow$.emit({
        value: false,
        type: "app-flow-diff",
        //flow: this.flow
    });
  }

  swap() {
    const env1 = this.environmentLeft();
    const env2 = this.environmentRight();
    const flow1 = this.flowLeft();
    const flow2 = this.flowRight();
    const ver1 = this.versionLeft;
    const ver2 = this.versionRight;

    this.environmentLeft.set(env2);
    this.environmentRight.set(env1);
    this.flowLeft.set(flow2);
    this.flowRight.set(flow1);
    this.versionLeft = ver2;
    this.versionRight = ver1;

    this.load();
  }

  async save() {
    try {

      const dialog: DialogRef = this.dialogService.open({
          title: "Save",
          content: "Save will overrite the flow on the right hand side",
          // content: `Are you sure - we will turn ON the selected ${content}`,
          actions: [
              { text: 'Yes', primary: true },
              { text: 'No' }
          ],
          width: 450,
          // height: 200,
          minWidth: 250
      });
      var result: any = await dialog.result.toPromise();

      if (result.text != 'Yes') {
          return;
      }

      this.ready.set(false);
      //var flowData :any = this.editor.get();
      //var code = this.editor.getValue();
      //debugger;

      // this.code = this.model.value;
      let definition;
      try {
        definition = JSON.parse(this.editor.getModifiedEditor().getValue());
        //definition = JSON.parse(this.modifiedModel.code);
      }
      catch (error) {
          this.error = error;
          return;
      }

      const flow = await this.flowManagementService.getFlow(this.environmentRight(), this.flowRight()).toPromise();
      flow.properties.definition = definition;

      this.flowManagementService.updateFlow(this.environmentRight(), this.flowRight(), flow).toPromise().then((response) => {
          //alert('done!');
          // this.close();
      }, (response) => {
          this.error = response.error || response;
      });
    }
    catch {

    }
    finally {
          this.ready.set(true);
    }
  }

}
